import React, { useState } from 'react';
import '../../styles/PagesStyles/Career.css';
import { trackEvent } from "../../utils/google-analytics";

// Import images
import brainIcon from '../../assets/images/Career/brain.png';
import cultureIcon from '../../assets/images/Career/culture.png';
import growIcon from '../../assets/images/Career/grow.png';
import workIcon from '../../assets/images/Career/work.png';
import usePageTitle from "../../hooks/usePageTitle";


const Career = () => {

  usePageTitle("Combat Solutions AI | Career");

  const [showAllJobs, setShowAllJobs] = useState(false);

  const jobOpenings = [
    { 
      id: 1,
      title: "Associate AI Developer / GenAI Developer",
      skills: [
        "LLM prompting, fine-tuning, evaluation",
        "RAG, embeddings, vector DB integration",
        "GenAI app development (LangChain/LlamaIndex)",
        "Model deployment & monitoring",
        "API integration (FastAPI/Flask), cloud basics"
      ],
      experience: "0-2 Years"

    },
    {
      id: 2,
      title: "Associate Backend Engineer",
      skills: [
        "Backend development using Python (FastAPI) / Node.js", 
        "Build REST APIs, microservices",
        "Database design & query optimization",
        "Integrations with third-party services",
        "Docker, Git, basic cloud deployment"
      ],
      experience: "1+ Years"
    },
//     {
//       id: 3,
//       title: "Full Stack Engineer",
// ●	Docker, Git, basic cloud deployment

//       ],
//       experience: "1+ Years"


//     },
    {
      id: 3,
      title: "Full Stack Engineer",
      skills: [
        "Frontend development (React, TypeScript)",     
        "API integration & state management",
        "Backend support (Node/Python), server logic",
        "Real-time features (WebSockets/streaming)",
        "Performance, responsive UI & debugging"
      ],
      experience: "1+ Years"

    },
    {
      id: 4,
      title: "AI Research Intern",
      skills: [
        "Research in NLP / GenAI concepts",
        "Implement prototypes & experiments",
        "Model testing, benchmarking & reporting",
        "Paper reading & reproduction",
        "Support team on innovation tasks"
      ],
      experience: "Fresher"

    },
    {
      id: 5,
      title: "Team Lead - GenAI Solutions",
      skills: [
        "Lead GenAI solution design & delivery",
        "LLM integration + RAG architecture",
        "Backend/API development & deployment",
        "Optimization, security & monitoring",
        "Team mentoring & project ownership"
      ],
      experience: "2+ Years"

    },
    {
      id: 6,
      title: "Accountant",
      skills: [
        "Bookkeeping & accounting entries",
        "Billing, invoicing & documentation",
        "Bank reconciliation & payment tracking",
        "GST/TDS support & compliance",
        "Financial reports & record maintenance"
      ],
      experience: "0-1 Years"

    }
  ];

  const whyJoinItems = [
    {
      title: "Work on Real AI Systems",
      description:
        "Design and deploy production-grade AI systems used by enterprises in regulated and data-intensive environments built for scale, security, and real impact.",
      icon: workIcon
    },
    {
      title: "Solve Hard Problems",
      description:
        "Tackle complex challenges across agentic workflows, document intelligence, multimodal AI, and governed automation that demand deep thinking and strong engineering.",
      icon: brainIcon
    },
    {
      title: "Learn & Grow",
      description:
        "Continuously evolve your skills through hands-on work with modern AI stacks, architectural ownership, and a culture that encourages experimentation and learning.",
      icon: growIcon
    },
    {
      title: "Culture of Engineering Excellence",
      description:
        "Work in small, high-performance teams that value clarity, quality, technical rigor, and ownership where good ideas are heard and great execution matters.",
      icon: cultureIcon
    }
  ];

  const handleApplyClick = (jobTitle) => {
    // Track the application click
    trackEvent("apply_click", {
      page: "Career Page",
      job_title: jobTitle,
    });

    const subject = `Application for ${jobTitle} Position`;

  const body = `Dear Hiring Team,

I am writing to apply for the ${jobTitle} position at Combat Solutions.

- Role of interest: ${jobTitle}
- Relevant projects/GitHub links: [Please add your links]
- Why I want to work with Combat Solutions : [Brief explanation]

Best regards,
[Your Name]
`;

  const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=manager@combatsolutions.co.in&su=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;

  window.open(gmailURL, "_blank");
};



  return (
    <div className="career-page">
      {/* Hero Section */}
      <section className="career-hero">
        <div className="career-hero-content">
          <h1 className="career-hero-title">Build Your Career With Us</h1>
          <p className="career-hero-subtitle">
            Develop your skills by designing and deploying intelligent systems
            that make a measurable impact across real business environments
          </p>
          <div className="career-hero-cta">
            <button
              className="career-cta-button primary"
              onClick={() => {
                document
                  .getElementById("opportunities")
                  .scrollIntoView({ behavior: "smooth" });

                trackEvent("cta_click", {
                  page: "Career Page",
                  button_text: "View Open Roles",
                });
              }}
            >
              View Open Roles
            </button>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="career-why-join-section">
        <div className="career-why-join-container">
          <div className="career-why-join-header">
            <h2 className="career-why-join-title">
              Why Join Combat Solutions?
            </h2>
            <p className="career-why-join-subtitle">
              Be part of something transformative
            </p>
          </div>

          <div className="career-why-join-grid">
            {whyJoinItems.map((item, index) => (
              <div
                className="career-why-join-card"
                key={index}
                onClick={() =>
                  trackEvent("why_join_click", {
                    page: "Career Page",
                    card_title: item.title,
                  })
                }
              >
                <div className="career-why-join-card-icon">
                  <img src={item.icon} alt={item.title} />
                </div>
                <h3 className="career-why-join-card-title">{item.title}</h3>
                <p className="career-why-join-card-description">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="career-opportunities" id="opportunities">
        <div className="career-opportunities-container">
          <div className="career-opportunities-header">
            <h2 className="career-opportunities-title">
              Career Opportunities
            </h2>
            <p className="career-opportunities-subtitle">
              Find roles where your skills create real impact
            </p>
            <span className="count-badge">
              {jobOpenings.length} Open Positions
            </span>
          </div>

          <div className="career-jobs-grid">
            {jobOpenings
              .slice(0, showAllJobs ? jobOpenings.length : 3)
              .map((job) => (
                <div className="career-job-card" key={job.id}>
                  <div className="career-job-header">
                    <h3 className="career-job-title">{job.title}</h3>
                    <span className="career-job-id">
                      ID: CS-{job.id.toString().padStart(3, "0")}
                    </span>
                  </div>

                  <p className="career-job-experience">
                    Experience Required: {job.experience}
                  </p>

                  <ul>
                    {job.skills.map((skill, i) => (
                      <li key={i}>{skill}</li>
                      
                    ))}
                  </ul>

                  <div className="career-job-apply">
                    <button
                      className="career-apply-button"
                      onClick={() => handleApplyClick(job.title)}
                    >
                      Apply via Email
                    </button>
                  </div>
                </div>
              ))}
          </div>

          {/* See More / Show Less */}
          {jobOpenings.length > 2 && (
            <div className="career-see-more">
              <button
                className="career-see-more-btn"
                onClick={() => {
                  setShowAllJobs(!showAllJobs);
                  trackEvent("toggle_jobs", {
                    page: "Career Page",
                    action: showAllJobs ? "Show Less" : "See More",
                  });
                }}
              >
                {showAllJobs ? "Show Less Positions" : "See More Positions"}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Career;
